import React, { useEffect, useId, useMemo, useState } from 'react';
import { classifyPlanDeComSupport } from '../../../utils/planDeComSupportUtils';
import { formatEurCell } from '../../../utils/adminBulkPriceAdjust';
import kkpService from '../../../services/kkpService';
import pdcService from '../../../services/pdcService';
import { notify } from '../../common/AppToast';
import {
  AppButton,
  AppCheckbox,
  AppEmptyState,
  AppInput,
  AppModal,
  AppSpinner,
} from '../../ui';

const HIDDEN_CATEGORIES = new Set(['type_campagne', 'remise_volume', 'remise_volume_news']);

const GROUP_META = [
  { key: 'mag', label: 'Magazine', hint: 'Formats papier' },
  { key: 'reportage', label: 'Reportages (Print + web)', hint: 'Feuilletons multi-diffusion' },
  { key: 'set', label: 'Set de table', hint: 'Emplacements sur le set de table' },
  { key: 'web', label: 'Web', hint: 'Site et réseaux' },
  { key: 'newsletter', label: 'Newsletters', hint: 'Emplacements newsletters' },
  { key: 'packasso', label: 'Pack Asso', hint: 'Formules association, print et digital' },
  { key: 'frais', label: 'Frais techniques', hint: 'Création, rédaction, options' },
  { key: 'autres', label: 'Autres prestations', hint: 'Prestations complémentaires' },
  { key: 'klub', label: 'Le Klub', hint: 'Abonnements par tranche de salariés' },
];

const groupKeyForTarif = (tarif) => {
  if (tarif.categorie === 'pack_asso') return 'packasso';
  if (tarif.categorie === 'frais') return 'frais';
  const kind = classifyPlanDeComSupport(tarif.libelle, tarif.categorie);
  if (GROUP_META.some((group) => group.key === kind)) return kind;
  return 'autres';
};

const formatTarifPrice = (tarif) => {
  const raw = tarif.effective_price ?? tarif.valeur_numerique;
  const label = formatEurCell(raw);
  if (/\/\s*km/i.test(String(tarif.libelle || ''))) return `${label} / km`;
  return label;
};

const klubSummary = (offer) => {
  const parts = [
    ['1–10', offer.tarif_1_10],
    ['11–20', offer.tarif_11_20],
    ['21–48', offer.tarif_21_48],
    ['49+', offer.tarif_49_plus],
  ]
    .filter(([, value]) => value != null && value !== '' && Number.isFinite(Number(value)))
    .map(([label, value]) => `${label} ${formatEurCell(value)}`);
  if (offer.frais_initial_ht != null && offer.frais_initial_ht !== '' && Number.isFinite(Number(offer.frais_initial_ht))) {
    parts.push(`entrée ${formatEurCell(offer.frais_initial_ht)}`);
  }
  return parts.join(' · ');
};

const buildExportItems = (tarifs, offers) => {
  const items = (tarifs || [])
    .filter((tarif) => {
      if (tarif.validity_status !== 'active') return false;
      if (HIDDEN_CATEGORIES.has(tarif.categorie)) return false;
      const price = tarif.effective_price ?? tarif.valeur_numerique;
      return price != null && price !== '' && Number.isFinite(Number(price));
    })
    .map((tarif) => ({
      key: `tarif:${tarif.id}`,
      kind: 'tarif',
      id: tarif.id,
      group: groupKeyForTarif(tarif),
      label: tarif.libelle,
      priceLabel: formatTarifPrice(tarif),
    }));

  (offers || [])
    .filter((offer) => offer.validity_status === 'active' && klubSummary(offer) !== '')
    .forEach((offer) => {
      const name = String(offer.nom || offer.code || 'Offre Klub').trim();
      items.push({
        key: `klub:${offer.id}`,
        kind: 'klub',
        id: offer.id,
        group: 'klub',
        label: name,
        priceLabel: klubSummary(offer),
      });
    });

  return items;
};

const KkpAdminPriceExportModal = ({ show, onClose }) => {
  const baseId = useId();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const [tarifsPayload, offers] = await Promise.all([
          pdcService.listAdminTarifs(),
          kkpService.listKlubOffersAdmin(),
        ]);
        if (cancelled) return;
        const nextItems = buildExportItems(tarifsPayload.tarifs, offers);
        setItems(nextItems);
        setSelected(new Set(nextItems.map((item) => item.key)));
        setQuery('');
      } catch (err) {
        if (!cancelled) setError(err.message || 'Impossible de charger les tarifs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show]);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleItems = useMemo(() => {
    if (!normalizedQuery) return items;
    return items.filter((item) => item.label.toLowerCase().includes(normalizedQuery));
  }, [items, normalizedQuery]);

  const groups = useMemo(() => GROUP_META
    .map((meta) => ({
      ...meta,
      items: visibleItems.filter((item) => item.group === meta.key),
    }))
    .filter((group) => group.items.length > 0), [visibleItems]);

  const selectedCount = items.filter((item) => selected.has(item.key)).length;
  const visibleSelectedCount = visibleItems.filter((item) => selected.has(item.key)).length;

  const toggleKey = (key) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleKeys = (keys, shouldSelect) => {
    setSelected((current) => {
      const next = new Set(current);
      keys.forEach((key) => {
        if (shouldSelect) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  const handleExport = async () => {
    const chosen = items.filter((item) => selected.has(item.key));
    if (chosen.length === 0) {
      notify.error('Sélectionnez au moins un produit.');
      return;
    }

    setExporting(true);
    try {
      await pdcService.downloadGrilleTarifairePdf({
        tarifIds: chosen.filter((item) => item.kind === 'tarif').map((item) => item.id),
        klubOfferIds: chosen.filter((item) => item.kind === 'klub').map((item) => item.id),
      });
      notify.success('Grille tarifaire téléchargée');
      onClose?.();
    } catch (err) {
      notify.error(err.message || 'Export PDF impossible');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppModal
      show={show}
      title="Exporter les prix en PDF"
      onClose={onClose}
      size="lg"
      centered
      scrollable
      closeDisabled={exporting}
      bodyClassName="pt-2"
      footer={(
        <>
          <span className="me-auto text-secondary-light small">
            {selectedCount} produit{selectedCount > 1 ? 's' : ''} sélectionné{selectedCount > 1 ? 's' : ''}
          </span>
          <AppButton type="button" variant="outline-secondary" onClick={onClose} disabled={exporting}>
            Annuler
          </AppButton>
          <AppButton
            type="button"
            variant="danger-600"
            icon="mdi:file-pdf-box"
            loading={exporting}
            disabled={loading || selectedCount === 0}
            onClick={handleExport}
          >
            Télécharger le PDF
          </AppButton>
        </>
      )}
    >
      <p className="text-secondary-light small mb-3">
        Choisissez les produits à inclure. Le PDF reprend l’identité Koikispass et les tarifs actifs, prêts à envoyer au client.
      </p>

      {loading && <AppSpinner />}

      {!loading && error && (
        <AppEmptyState icon="mdi:alert-circle-outline" message="Tarifs indisponibles" description={error} />
      )}

      {!loading && !error && (
        <>
          <AppInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher un produit…"
            aria-label="Rechercher un produit"
            className="mb-3"
            disabled={exporting}
          />
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <AppCheckbox
              id={`${baseId}-all`}
              label="Tout sélectionner"
              checked={visibleItems.length > 0 && visibleSelectedCount === visibleItems.length}
              indeterminate={visibleSelectedCount > 0 && visibleSelectedCount < visibleItems.length}
              disabled={exporting || visibleItems.length === 0}
              onChange={(event) => toggleKeys(visibleItems.map((item) => item.key), event.target.checked)}
            />
            <span className="text-secondary-light small">{visibleItems.length} affiché{visibleItems.length > 1 ? 's' : ''}</span>
          </div>

          {groups.length === 0 && (
            <AppEmptyState
              icon="mdi:package-variant"
              message="Aucun produit"
              description={normalizedQuery ? 'Aucun libellé ne correspond à cette recherche.' : 'Aucun tarif actif à exporter.'}
            />
          )}

          {groups.map((group) => {
            const groupSelected = group.items.filter((item) => selected.has(item.key)).length;
            return (
              <div key={group.key} className="border rounded mb-3 overflow-hidden">
                <div className="d-flex flex-wrap align-items-center gap-2 px-3 py-2 border-bottom bg-light">
                  <AppCheckbox
                    id={`${baseId}-${group.key}`}
                    checked={groupSelected === group.items.length}
                    indeterminate={groupSelected > 0 && groupSelected < group.items.length}
                    disabled={exporting}
                    onChange={(event) => toggleKeys(group.items.map((item) => item.key), event.target.checked)}
                    aria-label={`Sélectionner ${group.label}`}
                  />
                  <div className="flex-grow-1">
                    <div className="fw-semibold">{group.label}</div>
                    {group.hint ? <div className="text-secondary-light small">{group.hint}</div> : null}
                  </div>
                  <span className="text-secondary-light small">{groupSelected}/{group.items.length}</span>
                </div>
                {group.items.map((item, index) => (
                  <div key={item.key} className={`d-flex align-items-start gap-2 px-3 py-2${index < group.items.length - 1 ? ' border-bottom' : ''}`}>
                    <AppCheckbox
                      id={`${baseId}-${item.key}`}
                      checked={selected.has(item.key)}
                      disabled={exporting}
                      onChange={() => toggleKey(item.key)}
                      aria-label={item.label}
                      wrapperClassName="mt-1"
                    />
                    <label htmlFor={`${baseId}-${item.key}`} className="flex-grow-1 mb-0">
                      {item.label}
                    </label>
                    <span className="text-end small fw-semibold w-50">
                      {item.priceLabel}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
        </>
      )}
    </AppModal>
  );
};

export default KkpAdminPriceExportModal;
